/**
 * Client-side image compression and Data URL optimization utility.
 * Bypasses cloud storage CORS restrictions and provides instant local uploads.
 */

export interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: 'image/jpeg' | 'image/webp' | 'image/png';
}

export async function optimizeImageToDataUrl(
  fileOrBase64: File | string,
  options: ImageOptimizationOptions = {}
): Promise<string> {
  const {
    maxWidth = 1000,
    maxHeight = 1000,
    quality = 0.85,
    mimeType = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    
    if (typeof fileOrBase64 === 'string' && fileOrBase64.startsWith('http')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        if (width === 0 || height === 0) {
          // If image dimensions could not be read, fallback to original if string
          if (typeof fileOrBase64 === 'string') {
            resolve(fileOrBase64);
          } else {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(fileOrBase64);
          }
          return;
        }

        // Calculate scaling ratio
        let scale = 1;
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          scale = Math.min(widthRatio, heightRatio);
        }

        const targetWidth = Math.max(1, Math.round(width * scale));
        const targetHeight = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d', { alpha: mimeType === 'image/png' });
        if (!ctx) {
          throw new Error('Canvas 2D context not available');
        }

        // If JPEG, fill white background for transparent PNG inputs
        if (mimeType === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, targetWidth, targetHeight);
        }

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Export compressed Data URL
        let dataUrl = canvas.toDataURL(mimeType, quality);
        
        // If webp is not supported by browser, fallback to jpeg
        if (mimeType === 'image/webp' && !dataUrl.startsWith('data:image/webp')) {
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      } catch (err) {
        console.warn('Canvas optimization failed, falling back to FileReader:', err);
        if (typeof fileOrBase64 === 'string') {
          resolve(fileOrBase64);
        } else {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(fileOrBase64);
        }
      }
    };

    img.onerror = (err) => {
      console.warn('Image load failed during optimization, falling back:', err);
      if (typeof fileOrBase64 === 'string') {
        resolve(fileOrBase64);
      } else {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(err);
        reader.readAsDataURL(fileOrBase64);
      }
    };

    if (typeof fileOrBase64 === 'string') {
      img.src = fileOrBase64;
    } else {
      const objectUrl = URL.createObjectURL(fileOrBase64);
      
      const originalOnLoad = img.onload;
      img.onload = (e) => {
        URL.revokeObjectURL(objectUrl);
        if (typeof originalOnLoad === 'function') {
          originalOnLoad.call(img, e);
        }
      };
      
      img.src = objectUrl;
    }
  });
}
